import { useNavigate, Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Lock, Menu, X } from "lucide-react";

// --- STYLED COMPONENTS DLA MENU ---

const NavHeader = styled.header`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); /* Delikatny, nowoczesny cień */
`;

// Pasek admina w kolorach PTH (Granat i Czerwień)
const AdminStatusBar = styled.div`
  background: #00205B;
  color: #fff;
  padding: 8px 5%;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: center;
  letter-spacing: 1px;
`;

const AdminBtn = styled.button`
  background: transparent;
  border: 1px solid ${props => props.color || '#fff'};
  color: ${props => props.color || '#fff'};
  padding: 3px 10px;
  font-size: 10px;
  font-weight: bold;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.color || '#fff'};
    color: #fff;
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;

  @media (max-width: 800px) {
    height: 80px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  z-index: 1002;

  img {
    height: 80px;
    width: auto;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }

  @media (max-width: 800px) {
    img {
      height: 60px;
    }
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 35px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const NavLinkBtn = styled.button`
  background: transparent;
  border: none;
  color: #00205B; /* Granatowy z logo */
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  position: relative;
  padding: 8px 0;
  transition: color 0.3s ease;

  /* Czerwone podkreślenie na hover */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: #C8102E; /* Czerwony z logo */
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: #C8102E;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const MobileToggleBtn = styled.button`
  display: none;
  background: none;
  border: none;
  color: #00205B;
  cursor: pointer;
  z-index: 1002;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.9);
  }

  @media (max-width: 800px) {
    display: block;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 32, 91, 0.4); /* Półprzezroczysty granat */
  opacity: ${props => props.open ? '1' : '0'};
  visibility: ${props => props.open ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const MobileMenuPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${props => props.open ? '0' : '-320px'};
  width: 300px;
  max-width: 80vw;
  height: 100vh;
  background: #ffffff;
  box-shadow: -5px 0 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  padding-top: 100px;
  transition: right 0.4s cubic-bezier(0.77, 0.2, 0.05, 1);
  z-index: 1001;
`;

const MobileLink = styled.button`
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  color: #00205B;
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 20px 30px;
  text-align: left;
  cursor: pointer;
  transition: background 0.3s, color 0.3s, padding-left 0.3s;

  &:hover {
    background: #fcfcfc;
    color: #C8102E;
    padding-left: 35px;
  }
`;

// --- KOMPONENT NAVBAR ---

export const MenuBar = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const isAdmin = !!localStorage.getItem("adminToken");

    const menuItems = [
        { label: "Home", path: "/" },
        { label: "Aktualności", path: "/aktualnosci" },
        { label: "O Klubie", path: "/kim-jestesmy" },
        { label: "Kontakt", path: "/kontakt" },
    ];

    // Blokada scrollowania strony, gdy otwarte jest menu mobilne
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; }
    }, [menuOpen]);

    const handleLogout = () => {
        localStorage.removeItem("adminToken");
        window.location.reload();
    };

    const handleNavigate = (path) => {
        navigate(path);
        setMenuOpen(false);
        window.scrollTo(0, 0);
    };

    return (
        <NavHeader>
            {/* PASEK ADMINA */}
            {isAdmin && (
                <AdminStatusBar>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Lock size={12} /> Tryb Edycji Aktywny
                    </span>
                    <div>
                        <AdminBtn color="#fff" onClick={() => handleNavigate("/admin")}>
                            PANEL
                        </AdminBtn>
                        <AdminBtn color="#ff4d4d" onClick={handleLogout} style={{ borderColor: '#ff4d4d' }}>
                            WYLOGUJ
                        </AdminBtn>
                    </div>
                </AdminStatusBar>
            )}

            {/* GŁÓWNY PASEK NAWIGACJI */}
            <NavContainer>
                <LogoLink to="/" onClick={() => setMenuOpen(false)}>
                    <img src="./logo_PTH.png" alt="Logo PTH Poznań" />
                </LogoLink>

                {/* NAWIGACJA DESKTOP */}
                <DesktopNav>
                    {menuItems.map((item) => (
                        <NavLinkBtn key={item.path} onClick={() => handleNavigate(item.path)}>
                            {item.label}
                        </NavLinkBtn>
                    ))}
                </DesktopNav>

                {/* PRZYCISK HAMBURGERA (MOBILE) */}
                <MobileToggleBtn onClick={() => setMenuOpen(!menuOpen)}>
                    {menuOpen ? <X size={32} /> : <Menu size={32} />}
                </MobileToggleBtn>
            </NavContainer>

            {/* OVERLAY I MENU MOBILNE */}
            <MobileMenuOverlay open={menuOpen} onClick={() => setMenuOpen(false)} />
            <MobileMenuPanel open={menuOpen}>
                {menuItems.map((item) => (
                    <MobileLink key={item.path} onClick={() => handleNavigate(item.path)}>
                        {item.label}
                    </MobileLink>
                ))}
                
                {isAdmin && (
                    <MobileLink 
                        style={{ color: '#C8102E', marginTop: '20px', borderTop: '2px solid #f0f0f0' }} 
                        onClick={() => handleNavigate("/admin")}
                    >
                        Panel Sterowania
                    </MobileLink>
                )}
            </MobileMenuPanel>
        </NavHeader>
    );
};